import type { LoadedHuman } from "./assetLoader";
import type { Clock } from "./clock";
import { layerClockTime } from "./clock";

/** Drive glTF clip mixers from the shared Clock. */
export class AnimationPlayer {
  private humans: LoadedHuman[] = [];
  /** Prior clock.time for detecting loop wrap (time jumps backward). */
  private lastClockTime = 0;

  setHumans(humans: LoadedHuman[]): void {
    this.humans = humans;
    this.lastClockTime = 0;
  }

  private activeHumans(excludeIds?: ReadonlySet<string> | null): LoadedHuman[] {
    if (!excludeIds || excludeIds.size === 0) return this.humans;
    return this.humans.filter((h) => !excludeIds.has(h.def.id));
  }

  /**
   * Absolute snap of actions to clock.time.
   * Works while playing or paused (seek must not be a no-op during play).
   * `excludeIds`: skip humans owned by dynamic ragdoll (H3 tier gate).
   */
  snapToClock(clock: Clock, excludeIds?: ReadonlySet<string> | null): void {
    for (const human of this.activeHumans(excludeIds)) {
      if (!human.mixer) continue;
      for (const action of human.actions.values()) {
        const clip = action.getClip();
        const t = layerClockTime(clock.time, human.timeOffset ?? 0, clip.duration);
        action.time = t;
        action.paused = !clock.playing;
        action.play();
      }
      human.mixer.update(0);
    }
    this.lastClockTime = clock.time;
  }

  /**
   * Advance mixers. Optional `excludeIds` keeps dynamic ragdoll humans free of clip writes.
   */
  update(clock: Clock, dt: number, excludeIds?: ReadonlySet<string> | null): void {
    if (!clock.playing) {
      this.snapToClock(clock, excludeIds);
      return;
    }

    // Loop reset (or any backward jump): re-align mixers to absolute clock.
    if (clock.time + 1e-6 < this.lastClockTime) {
      this.snapToClock(clock, excludeIds);
      return;
    }

    for (const human of this.activeHumans(excludeIds)) {
      if (!human.mixer) continue;
      for (const action of human.actions.values()) {
        action.paused = false;
      }
      human.mixer.update(dt * clock.speed);
    }
    this.lastClockTime = clock.time;
  }

  play(): void {
    for (const human of this.humans) {
      for (const action of human.actions.values()) {
        action.paused = false;
      }
    }
  }

  pause(): void {
    for (const human of this.humans) {
      for (const action of human.actions.values()) {
        action.paused = true;
      }
    }
  }
}
